import { z } from "zod";
import type { SpaceNavigation } from "./domain.js";

export const WorkspaceAttentionItemSchema = z.object({
  key: z.string(),
  spaceId: z.string(),
  name: z.string(),
  preview: z.string(),
  color: z.string().nullable(),
  reason: z.enum(["decision", "waiting", "failed"]),
  updatedAt: z.string(),
  target: z.union([
    z.object({ missionId: z.string() }),
    z.object({ botId: z.string(), threadId: z.string().optional() }),
    z.object({ groupId: z.string(), threadId: z.string().optional() }),
  ]),
});
export type WorkspaceAttentionItem = z.infer<typeof WorkspaceAttentionItemSchema>;
export type WorkspaceInboxEntry = {
  key: string;
  name: string;
  preview: string;
  unread: boolean;
  updatedAt: string;
  color: string | null;
  space: string;
  attention?: WorkspaceAttentionItem["reason"];
  target: { spaceId: string; name: string } & WorkspaceAttentionItem["target"];
};

/** Shared unread conversation state across the account's accessible spaces. */
export function workspaceNotificationInbox(
  navigation: SpaceNavigation,
  attention: WorkspaceAttentionItem[] = [],
  cleared: Array<{ spaceId: string; before: string }> = [],
): WorkspaceInboxEntry[] {
  const spaces = navigation.spaces.map((space) =>
    space.id === navigation.current.id ? { ...space, ...navigation.current } : space,
  );
  const conversations: WorkspaceInboxEntry[] = spaces
    .flatMap((space) => [
      ...space.bots.map((bot) => ({
        key: `bot:${bot.id}`,
        name: bot.name,
        preview: bot.preview ?? "",
        unread: bot.unread,
        updatedAt: bot.updatedAt ?? "",
        color: bot.color,
        space: space.name,
        target: { botId: bot.id, spaceId: space.id, name: bot.name },
      })),
      ...space.groups.map((group) => ({
        key: `group:${group.id}`,
        name: group.name,
        preview: group.preview ?? "",
        unread: group.unread,
        updatedAt: group.updatedAt ?? "",
        color: null,
        space: space.name,
        target: { groupId: group.id, spaceId: space.id, name: group.name },
      })),
    ])
    .filter((entry) => entry.unread || entry.preview.trim())
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const entries = new Map(conversations.map((entry) => [entry.key, entry]));
  for (const item of attention) {
    const space = spaces.find((space) => space.id === item.spaceId);
    if (!space) continue;
    const previous = entries.get(item.key);
    if (previous?.attention && previous.updatedAt > item.updatedAt) continue;
    entries.set(item.key, {
      key: item.key,
      name: previous?.name ?? item.name,
      preview: item.preview || previous?.preview || "",
      color: item.color,
      unread: true,
      space: space.name,
      updatedAt: item.updatedAt,
      attention: item.reason,
      target: { ...item.target, spaceId: item.spaceId, name: previous?.name ?? item.name },
    });
  }
  const cutoffs = new Map(cleared.map((item) => [item.spaceId, item.before]));
  return [...entries.values()]
    .map((entry) => ({
      ...entry,
      unread:
        entry.unread &&
        (!cutoffs.has(entry.target.spaceId) ||
          Date.parse(entry.updatedAt) > Date.parse(cutoffs.get(entry.target.spaceId)!)),
    }))
    .sort(
      (a, b) =>
        Number(Boolean(b.attention)) - Number(Boolean(a.attention)) ||
        b.updatedAt.localeCompare(a.updatedAt),
    );
}
